import { useEffect, useState } from 'react'
import { Search, X } from 'lucide-react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { displayMapper } from '@/lib/display-mapper'
import type { GroupListItem } from './types'
import {
  CLAIM_CHIPS,
  CLAIM_SORTS,
  TIME_WINDOWS,
  type ClaimSort,
  type TimeWindow,
} from './leak-site-utils'

/**
 * Workbench for the Leak Sites feed: time-window tabs, sector / country /
 * group filters (server-side), a sort control + quick chips + free-text
 * search (client-side over the loaded page), active-filter chips, and a
 * result count.
 *
 * Server-side axes (sector / country / group / window) drive the /claims
 * query and the authoritative `resultCount`. Search / sort / chips refine the
 * currently-loaded page; the count line says so honestly when they're active.
 */

export interface WorkbenchState {
  window: TimeWindow
  sort: ClaimSort
  search: string
  sector: string
  country: string
  group: string
  chips: Record<string, boolean>
}

export const ALL_SENTINEL = 'all'

export interface LeakSitesWorkbenchProps {
  value: WorkbenchState
  onChange: (next: WorkbenchState) => void
  sectorOptions: ReadonlyArray<{ slug: string; name: string }>
  groupOptions: ReadonlyArray<GroupListItem>
  /** Authoritative server total for the active server-side filters. */
  resultCount: number
  /** Rows visible after client-side search / sort / chips on the loaded page. */
  shownCount: number
}

export function LeakSitesWorkbench({
  value,
  onChange,
  sectorOptions,
  groupOptions,
  resultCount,
  shownCount,
}: LeakSitesWorkbenchProps) {
  const [countryDraft, setCountryDraft] = useState(value.country)
  const [countryFocused, setCountryFocused] = useState(false)

  useEffect(() => {
    if (countryFocused) return
    if (value.country) {
      setCountryDraft(value.country)
      return
    }

    if (countryDraft.length === 2) {
      setCountryDraft('')
    }
  }, [countryDraft, countryFocused, value.country])

  const set = (patch: Partial<WorkbenchState>) => onChange({ ...value, ...patch })

  const selectField = (key: 'sector' | 'group', raw: string) =>
    set({ [key]: raw === ALL_SENTINEL ? '' : raw } as Partial<WorkbenchState>)

  const toggleChip = (chip: string) =>
    set({ chips: { ...value.chips, [chip]: !value.chips[chip] } })

  const groupName = (id: string) =>
    groupOptions.find((g) => g.id === id)?.name ?? id
  const sectorName = (slug: string) =>
    sectorOptions.find((s) => s.slug === slug)?.name ?? displayMapper.sector(slug)

  const activeChips = CLAIM_CHIPS.filter((c) => value.chips[c.value])
  const hasClientRefinement = value.search.trim().length > 0 || activeChips.length > 0

  // Active-filter chips (server + client) with their removal patches.
  const activeFilters: Array<{ key: string; label: string; clear: Partial<WorkbenchState> }> = []
  if (value.search.trim()) {
    activeFilters.push({ key: 'search', label: `“${value.search.trim()}”`, clear: { search: '' } })
  }
  if (value.sector) {
    activeFilters.push({ key: 'sector', label: `Sector: ${sectorName(value.sector)}`, clear: { sector: '' } })
  }
  if (value.country) {
    activeFilters.push({ key: 'country', label: `Region: ${value.country}`, clear: { country: '' } })
  }
  if (value.group) {
    activeFilters.push({ key: 'group', label: `Group: ${groupName(value.group)}`, clear: { group: '' } })
  }
  for (const c of activeChips) {
    activeFilters.push({
      key: c.value,
      label: c.label,
      clear: { chips: { ...value.chips, [c.value]: false } },
    })
  }

  const clearAll = () =>
    onChange({ ...value, search: '', sector: '', country: '', group: '', chips: {} })

  return (
    <section aria-label="Leak site filters" className="space-y-3">
      <div className="rounded-lg border border-border bg-card p-3">
        <div className="flex flex-wrap items-center gap-2">
          {/* time window */}
          <div
            className="inline-flex rounded-md border border-border bg-background p-0.5"
            role="group"
            aria-label="Time window"
          >
            {TIME_WINDOWS.map((w) => (
              <button
                key={w.value}
                type="button"
                aria-pressed={value.window === w.value}
                onClick={() => set({ window: w.value })}
                className={cn(
                  'rounded px-3 py-1 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  value.window === w.value
                    ? 'bg-secondary text-foreground'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {w.label}
              </button>
            ))}
          </div>

          <span className="mx-0.5 hidden h-5 w-px bg-border sm:block" aria-hidden="true" />

          {/* sector */}
          <Select
            value={value.sector || ALL_SENTINEL}
            onValueChange={(v) => selectField('sector', v)}
          >
            <SelectTrigger className="h-8 w-[170px] text-xs" aria-label="Filter by sector">
              <SelectValue placeholder="All sectors" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_SENTINEL}>All sectors</SelectItem>
              {sectorOptions.map((s) => (
                <SelectItem key={s.slug} value={s.slug}>
                  {s.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* country */}
          <Input
            placeholder="Region (ISO α2)"
            maxLength={2}
            className="h-8 w-[130px] text-xs uppercase"
            value={countryDraft}
            onFocus={() => setCountryFocused(true)}
            onBlur={() => setCountryFocused(false)}
            onChange={(e) => {
              const v = e.target.value.toUpperCase()
              setCountryDraft(v)
              set({ country: v.length === 2 ? v : '' })
            }}
            aria-label="Filter by country"
          />

          {/* group */}
          <Select
            value={value.group || ALL_SENTINEL}
            onValueChange={(v) => selectField('group', v)}
          >
            <SelectTrigger className="h-8 w-[180px] text-xs" aria-label="Filter by group">
              <SelectValue placeholder="All groups" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_SENTINEL}>All groups</SelectItem>
              {groupOptions.map((g) => (
                <SelectItem key={g.id} value={g.id}>
                  {g.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* sort */}
          <div className="ml-auto flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Sort</span>
            <Select value={value.sort} onValueChange={(v) => set({ sort: v as ClaimSort })}>
              <SelectTrigger className="h-8 w-[150px] text-xs" aria-label="Sort claims">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CLAIM_SORTS.map((s) => (
                  <SelectItem key={s.value} value={s.value}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* search */}
        <div className="mt-2 flex items-center gap-2 rounded-md border border-border bg-background px-2.5 focus-within:ring-1 focus-within:ring-ring">
          <Search className="h-3.5 w-3.5 flex-none text-muted-foreground" aria-hidden="true" />
          <input
            type="search"
            value={value.search}
            onChange={(e) => set({ search: e.target.value })}
            placeholder="Search victim, group, source, CVE, sector, region…"
            aria-label="Search loaded claims"
            className="h-8 w-full bg-transparent text-xs outline-none placeholder:text-muted-foreground"
          />
        </div>

        {/* quick chips */}
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          {CLAIM_CHIPS.map((c) => {
            const on = Boolean(value.chips[c.value])
            return (
              <button
                key={c.value}
                type="button"
                aria-pressed={on}
                onClick={() => toggleChip(c.value)}
                className={cn(
                  'rounded-md border px-2.5 py-1 text-[11px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  on
                    ? 'border-primary/40 bg-primary/10 text-primary'
                    : 'border-border bg-background text-muted-foreground hover:text-foreground',
                )}
              >
                {c.label}
              </button>
            )
          })}
        </div>
      </div>

      {/* result count + active filters */}
      <div className="flex flex-wrap items-center gap-2 px-1 text-xs text-muted-foreground">
        <span>
          <span className="font-mono font-semibold text-foreground">
            {resultCount.toLocaleString('en-GB')}
          </span>{' '}
          {resultCount === 1 ? 'claim' : 'claims'} in window
          {hasClientRefinement ? (
            <span> · {shownCount} shown on this page</span>
          ) : null}
        </span>

        {activeFilters.length > 0 ? (
          <>
            <span className="h-3.5 w-px bg-border" aria-hidden="true" />
            {activeFilters.map((f) => (
              <button
                key={f.key}
                type="button"
                onClick={() => set(f.clear)}
                className="inline-flex items-center gap-1 rounded-md border border-primary/30 bg-primary/10 px-2 py-0.5 text-[11px] text-primary hover:bg-primary/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                // R28: carries the user's active filter VALUE in an attribute.
                data-ph-no-capture
                aria-label={`Remove filter ${f.label}`}
              >
                {f.label}
                <X className="h-3 w-3" aria-hidden="true" />
              </button>
            ))}
            <button
              type="button"
              onClick={clearAll}
              className="text-[11px] underline hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              Clear all
            </button>
          </>
        ) : null}
      </div>
    </section>
  )
}
